import { Typography } from "@mui/material";

import { Box } from "@mui/system";
import { useContext } from "react";

import { FormattedMessage } from "react-intl";

import BaseFormCard from "../../base/BaseFormCard";
import ArrowForwardIcon from "../../../assets/icons/ArrowForwardIcon";
import UserContext from "../../../services/user/user";

/**
 * Template-KPI-Vertical card chrome — overrides BaseFormCard's base chrome via
 * `paperStyle` (inline style, so it wins over the default border/radius/shadow).
 */
export const CARD_PAPER_STYLE = {
  borderRadius: "12px",
  boxShadow: "0 1px 3px 0 var(--10), 0 1px 2px -1px var(--10)",
  border: "1px solid var(--new-border)",
  backgroundColor: "var(--kp-is-fubctional-background-default)"
};

/**
 * Template-KPI-Vertical layout, passed via `paperSx`.
 * `position: relative` anchors the absolutely-positioned arrow.
 */
export const CARD_PAPER_SX = {
  minHeight: "121px",
  padding: "6px 0 0",
  position: "relative",
  marginBottom: "14px",
  cursor: "pointer"
};

/** `.Widget-Header` — header bar geometry (no bottom border). */
export const CARD_HEADER_SX = {
  height: "40px",
  p: "8px 12px",
  gap: "8px",
  display: "flex",
  alignItems: "center"
};

/** `.Headline` — title typography. Flexes so long names are not clipped. */
export const CARD_TITLE_SX = {
  flex: 1,
  fontFamily: "Inter",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: 1.5
};

/** card body — flex column anchored to the bottom of the card. */
export const CARD_BODY_SX = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  padding: "16px"
};

/**
 * `.Date` — primary content line, styled via the `.subscription-card-date`
 * class in theme.css (and its `.text-style-1` sub-line). Kept as an sx export
 * for PendingItem, which reuses the same type with the warning color.
 */
export const CARD_DATE_SX = {
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: "normal",
  lineHeight: 1.57,
  textAlign: "left",
  color: "var(--font-strong-default)"
};

/** arrow — fixed bottom-right, 20px from each edge; does not move with content. */
export const CARD_ARROW_SX = {
  position: "absolute",
  right: "20px",
  bottom: "20px",
  display: "flex",
  alignItems: "center"
};

export default function SubscribedItem({
  status,
  description,
  billingPeriodType,
  productId,
  price,
  paymentMethodDefault,
  isFreeTrial,
  onClick,
  itemId
}) {
  const { lang } = useContext(UserContext);
  function formatEuroAmount(bigDecimalEuro) {
    const euroAmount = parseFloat(bigDecimalEuro / 100);
    if (isNaN(euroAmount)) {
      return "";
    }

    let formattedAmount;

    if (euroAmount % 1 === 0 && euroAmount >= 10) {
      formattedAmount = euroAmount.toFixed(0);
    } else {
      formattedAmount =
        lang === "en"
          ? euroAmount.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })
          : euroAmount.toLocaleString("de-DE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
    }
    return formattedAmount + "€";
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.(event);
    }
  };

  return (
    <BaseFormCard
      noForm
      headerBorder={false}
      title={description}
      titleProps={{
        sx: CARD_TITLE_SX,
        "data-testid": `subscrioption-${itemId}-name`
      }}
      headerExtra={
        isFreeTrial && (
          <Box
            component="span"
            data-testid={`subscrioption-${itemId}-free-trial-badge`}
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              lineHeight: 1.5,
              padding: "2px 8px",
              borderRadius: "999px",
              backgroundColor: "var(--alert-background-warning)",
              color: "var(--font-strong-warning)"
            }}
          >
            <FormattedMessage id="subscription.freeTrialBadge" />
          </Box>
        )
      }
      paperStyle={CARD_PAPER_STYLE}
      paperSx={CARD_PAPER_SX}
      headerSx={CARD_HEADER_SX}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <Box sx={CARD_BODY_SX}>
        <span className="subscription-card-date">
          <span
            className="text-style-1"
            data-testid={`subscrioption-${itemId}-payment-cycle`}
            style={{ textTransform: "capitalize" }}
          >
            <FormattedMessage
              id={`amount_${billingPeriodType}`}
              values={{
                b: (...chunks) => <span>{formatEuroAmount(price)}</span>
              }}
            />
          </span>
          {paymentMethodDefault !== "-" && (
            <>
              <FormattedMessage id="paymentMethod" />
              <span>: </span>
              <span data-testid={`subscrioption-${itemId}-payment-method`}>
                <FormattedMessage id={paymentMethodDefault} />
              </span>
            </>
          )}
        </span>
      </Box>
      <Box sx={CARD_ARROW_SX} data-testid={`subscrioption-${itemId}-arrow-details`}>
        <ArrowForwardIcon />
      </Box>
    </BaseFormCard>
  );
}
